import React, { ReactElement, useState } from 'react';
import { Link, useHistory } from 'react-router-dom';
import { Auth } from '@aws-amplify/auth';
import './styles.scss';
import isEmpty from 'lodash/isEmpty';
import isUndefined from 'lodash/isUndefined';

import { useTranslate } from 'hooks/translate';

import siteLogoWhite from 'assets/svgs/logos/logo-bestpark-white.svg';
import siteLogoWhiteGreen from 'assets/svgs/logos/logo-white-green.svg';
import siteLogoBlue from 'assets/svgs/logos/logo-bestpark-blue.svg';
import iconUser from 'assets/svgs/iconmonstr-user-1.svg';
import iconLanguage from 'assets/svgs/icon-language.svg';
import Button from 'components/form/button/button';
import Drawer from 'components/animations/drawer';
import logoPhone from 'assets/svgs/icon-phone-black.svg';
import Select from 'components/form/select/select';
import useNav from 'hooks/nav';
import {
  CONTACT_US,
  OUR_STORY,
  PARKS_PATH,
  LOGIN_PATH,
  PROFILE_PATH,
  BESTPOINT_PATH,
  WHY_BESTPARK_PATH,
  INFO_PATH,
} from 'constants/paths';
import { TRANSLATIONS } from 'constants/locale';
import { useAuth } from 'contexts/auth-context';
import { useForm } from 'react-hook-form';
import { getPathWithoutLocale } from 'helpers/path-helpers';
import { eraseCookie } from 'helpers/cookie-helper';

/** Menu items. */
interface MenuItem {
  name: string;
  url: string;
  customClass?: string;
}

interface HeaderProps {
  theme?: 'white' | 'blue' | 'charcoal';
  position?: 'relative' | 'absolute';
}

const menuItems: MenuItem[] = [
  {
    name: 'parks',
    url: PARKS_PATH,
  },
  {
    name: 'bestpoint',
    url: BESTPOINT_PATH,
  },
  {
    name: 'why_bestpark',
    url: WHY_BESTPARK_PATH,
  },
  {
    name: 'our_story',
    url: OUR_STORY,
  },
  {
    name: 'info',
    url: INFO_PATH,
  },
  {
    name: 'contact_us',
    url: CONTACT_US,
  },
  {
    name: 'translations',
    url: PARKS_PATH,
    customClass: 'right translation',
  },
];

function SignOut(): ReactElement {
  const { t } = useTranslate();
  const { setAuthState } = useAuth();

  const doSignOut = async (evt: any): Promise<void> => {
    evt.preventDefault();
    localStorage.clear();
    eraseCookie('bestpark-user');
    await Auth.signOut();
    await setAuthState(undefined);
  };

  return (
    <span className='sign-out-text' onClick={doSignOut}>
      {t('signout')}
    </span>
  );
}

function UserBanner(): ReactElement {
  const { t, locale } = useTranslate();
  const { authState: auth } = useAuth();
  const { goTo } = useNav();

  const isLoggedin =
    !isUndefined(auth) && !isEmpty(auth) && !isUndefined(auth.userInfo);

  const loginLink = isLoggedin ? `${PROFILE_PATH}` : `${LOGIN_PATH}`;

  return (
    <div className='user-bar'>
      <div className='user'>
        <img
          onClick={() => {
            goTo(loginLink);
          }}
          src={iconUser}
          className='iconmonstr-user-1'
          alt='icon-user'
        />
        <span>
          {isLoggedin
            ? t('landing_page.header.user.hey', auth?.userInfo?.firstName ?? '')
            : t('landing_page.header.user.has_reservation')}
          <Link
            to={
              isLoggedin
                ? `/${locale}${PROFILE_PATH}`
                : `/${locale}${LOGIN_PATH}`
            }
            className='bold-link'
          >
            {isLoggedin
              ? t('landing_page.header.user.bookings')
              : t('login_button')}
          </Link>
        </span>
      </div>
      {isLoggedin && (
        <div className='logout'>
          <SignOut />
        </div>
      )}
    </div>
  );
}

export default function Header({
  theme = 'charcoal',
  position = 'relative',
}: HeaderProps): ReactElement {
  const [showMobileMenu, setShowMobileMenu] = useState(false);
  const { authState: auth } = useAuth();
  const { t } = useTranslate();
  const history = useHistory();
  const { changeLanguage, locale } = useNav();
  const { register } = useForm({
    defaultValues: {
      translate: `${locale}`,
    },
  });

  const isLoggedin =
    !isUndefined(auth) && !isEmpty(auth) && !isUndefined(auth.userInfo);

  const path = getPathWithoutLocale(history.location.pathname);

  const { REACT_APP_BESTPARK_WP = '' } = process.env;
  const BESTPARK_WP = `${REACT_APP_BESTPARK_WP}/${locale}`;

  return (
    <div className='header-component' style={{ position: position }}>
      {path !== PROFILE_PATH && path !== LOGIN_PATH && (
        <div className='user-wrap'>
          <UserBanner />
        </div>
      )}
      {/* Inner header wrapper. */}
      <div className='wrapper'>
        {/* Mobile menu button. Visibile on mobile only. */}
        <button
          className={`btn-mobile-menu open icon-menu ${theme}`}
          onClick={() => setShowMobileMenu(!showMobileMenu)}
        />
        {/* Site logo desktop. */}
        <a href={BESTPARK_WP} style={{ lineHeight: 0 }}>
          <img
            className='logo desktop'
            src={theme === 'white' ? siteLogoWhite : siteLogoBlue}
            alt='Bestpark Logo'
          />
        </a>
        <div className={`menu-items desktop ${theme}`}>
          {menuItems.map((menuItem: MenuItem, idx: Number) => {
            if (menuItem.name === 'translations') {
              return (
                <Select
                  key={`${idx}`}
                  id='translate '
                  label=''
                  name='translate'
                  registerRef={register}
                  options={TRANSLATIONS}
                  action={(e) => {
                    changeLanguage(e.target.value)();
                  }}
                  preIcon={
                    <img
                      src={iconLanguage}
                      className='icon-locale'
                      alt='icon-locale'
                    />
                  }
                />
              );
            }

            const customClass = menuItem.customClass ?? '';
            const menuURL = menuItem.url;
            const menuName = t(`landing_page.header.menus.${menuItem.name}`);
            return (
              <a
                key={`menu-item-desktop-${idx}`}
                className={`menu-item ${customClass} ${theme}`}
                href={`${
                  menuItem.name === 'home'
                    ? REACT_APP_BESTPARK_WP
                    : `${BESTPARK_WP}${menuURL}`
                }`}
              >
                {menuName}
              </a>
            );
          })}
        </div>

        <Button
          className='buy-ticket primary black'
          link={`${BESTPARK_WP}${PARKS_PATH}`}
          isExternalUrl
          openLinkInSameTab
        >
          <img src={logoPhone} alt='make a reservation' />
          <span>{t('landing_page.header.make_reservation')}</span>
        </Button>
      </div>

      {/* Header mobile menu. */}
      <Drawer className='menu-drawer' open={showMobileMenu}>
        <div className='mobile-menu'>
          {/* Close button. */}
          <button
            className='btn-mobile-menu close icon-cancel-1'
            onClick={() => setShowMobileMenu(!showMobileMenu)}
          />

          {/* Moobile menu site logo. */}
          <a href={BESTPARK_WP} style={{ lineHeight: 0 }}>
            <img
              className='logo mobile'
              src={siteLogoWhiteGreen}
              alt='Bestpark Logo'
            />
          </a>
          {/* Menu items. */}
          <div className='menu-items mobile'>
            {menuItems.map((menuItem: MenuItem, idx: Number) => {
              // Add key for empty elements also to avoid React warning
              if (menuItem.name === 'translations') {
                return <div key={`${idx}`} />;
              }
              const customClass = menuItem.customClass ?? '';
              const menuURL = menuItem.url;
              const menuName = t(`landing_page.header.menus.${menuItem.name}`);

              return (
                <a
                  key={`menu-item-mobile-${idx}`}
                  className={`menu-item ${customClass}`}
                  href={`${
                    menuItem.name === 'home'
                      ? REACT_APP_BESTPARK_WP
                      : `${BESTPARK_WP}${menuURL}`
                  }`}
                >
                  {menuName}
                </a>
              );
            })}

            <ul className='menu-lang'>
              {TRANSLATIONS.map((i, idx) => {
                return (
                  <li
                    className={locale === i.value ? 'bold' : ''}
                    key={`${idx}`}
                    onClick={() => {
                      changeLanguage(i.value)();
                    }}
                  >
                    {i.name}
                  </li>
                );
              })}
            </ul>
            <div className='user'>
              {isLoggedin ? (
                <>
                  <img
                    src={iconUser}
                    className='iconmonstr-user-1'
                    alt='icon-user'
                  />

                  <div className='user-details'>
                    {auth?.userInfo?.firstName} {auth?.userInfo?.lastName}
                    <Link
                      to={`/${locale}${PROFILE_PATH}`}
                      className='bold-link'
                    >
                      {t('landing_page.header.user.check_bookings')}
                    </Link>
                    <div className='logout'>
                      <SignOut />
                    </div>
                  </div>
                </>
              ) : (
                <Button
                  className='login-btn primary black'
                  link={`/${locale}${LOGIN_PATH}`}
                  openLinkInSameTab
                >
                  {t('login_button')}
                </Button>
              )}
            </div>
          </div>
        </div>
      </Drawer>
      {/* End header mobile menu. */}
    </div>
  );
}
